import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Loader2,
  CreditCard,
  AlertCircle,
  CheckCircle2,
  Coins,
} from "lucide-react";
import { toast } from "sonner";
import {
  confirmRazorpay,
  formatMoney,
  getPlanPreview,
  loadRazorpayScript,
  startCheckout,
} from "@/lib/payments";
import { getMyCredits } from "@/lib/referrals";

const PLAN_OPTIONS = [
  { value: "pay_in_full", label: "Pay in full" },
  { value: "part_payment", label: "Part payment" },
  { value: "monthly", label: "Monthly" },
];

export default function CheckoutDialog({
  enrollment,
  course,
  open,
  onClose,
  onPaymentComplete,
}) {
  const [plan, setPlan] = useState("pay_in_full");
  const [currency, setCurrency] = useState("INR");
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [applyCredits, setApplyCredits] = useState(false);
  const [credits, setCredits] = useState(null);

  // Which currencies actually have a non-zero price on this course.
  // A currency with amount<=0 is disabled in the picker (with tooltip) so
  // students can't accidentally pick a currency the course isn't priced in.
  const availableCurrencies = useMemo(() => {
    const pricing = course?.pricing || {};
    return {
      INR: Number(pricing.amount_inr || 0) > 0,
      USD: Number(pricing.amount_usd || 0) > 0,
    };
  }, [course]);

  // Load credit balance whenever the dialog opens.
  useEffect(() => {
    if (!open) return;
    getMyCredits()
      .then(setCredits)
      .catch(() => setCredits(null));
  }, [open]);

  // Pre-fill currency from course's primary currency if available AND
  // priced. Otherwise fall back to whichever currency is actually priced.
  useEffect(() => {
    if (open && course) {
      const primary = course.pricing?.currency_primary;
      if (primary === "INR" && availableCurrencies.INR) setCurrency("INR");
      else if (primary === "USD" && availableCurrencies.USD) setCurrency("USD");
      else if (availableCurrencies.INR) setCurrency("INR");
      else if (availableCurrencies.USD) setCurrency("USD");
    }
  }, [open, course, availableCurrencies]);

  // Refresh preview on plan/currency/enrollment change
  useEffect(() => {
    if (!open || !enrollment) return;
    let alive = true;
    setLoadingPreview(true);
    setError(null);
    setPreview(null);
    getPlanPreview(enrollment.id, plan, currency)
      .then((p) => {
        if (alive) setPreview(p);
      })
      .catch((e) => {
        if (alive)
          setError(
            e?.response?.data?.detail ||
              "Plan preview unavailable for this currency"
          );
      })
      .finally(() => alive && setLoadingPreview(false));
    return () => {
      alive = false;
    };
  }, [open, enrollment, plan, currency]);

  const nextIdx = preview?.next_due_index ?? 0;
  const nextInstallment =
    preview && nextIdx >= 0 ? preview.installments[nextIdx] : null;

  const totalActive = credits
    ? (currency === "INR"
        ? Number(credits.totals?.total_active_inr || 0)
        : Number(credits.totals?.total_active_usd || 0))
    : 0;
  const hasCreditsInCurrency = totalActive > 0;
  const nextAmount = nextInstallment?.amount || 0;
  const creditToApply = applyCredits
    ? Math.min(totalActive, nextAmount)
    : 0;
  const amountAfterCredits = Math.max(0, nextAmount - creditToApply);
  const isFreePath = applyCredits && amountAfterCredits <= 0.005 && nextAmount > 0;

  const startPayment = async () => {
    if (!enrollment || !preview || !nextInstallment) return;
    setSubmitting(true);
    try {
      const resp = await startCheckout(enrollment.id, {
        plan,
        currency,
        installment_index: nextIdx,
        apply_credits: applyCredits,
      });

      if (resp.provider === "free" && resp.status === "succeeded") {
        toast.success(
          `🎉 Paid with credits — ₹${resp.credits_applied?.toFixed(0) || ""} applied`
        );
        onPaymentComplete?.();
        onClose?.();
        return;
      }

      if (resp.provider === "stripe") {
        // Stripe → redirect to Checkout
        if (resp.checkout_url) {
          window.location.href = resp.checkout_url;
        }
        return;
      }

      // Razorpay → open inline modal
      await loadRazorpayScript();
      const rz = new window.Razorpay({
        key: resp.key_id,
        order_id: resp.order_id,
        amount: resp.amount,
        currency: resp.currency,
        name: "Knowledge is Key to Success",
        description: course?.title || "Course payment",
        handler: async (response) => {
          try {
            await confirmRazorpay(resp.payment_id, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            toast.success("Payment confirmed!");
            onPaymentComplete?.();
            onClose?.();
          } catch (e) {
            toast.error(
              e?.response?.data?.detail ||
                "Could not verify payment — contact admin"
            );
          }
        },
        modal: {
          ondismiss: () => {
            setSubmitting(false);
          },
        },
        theme: { color: "#d97706" },
      });
      rz.open();
    } catch (e) {
      const detail = e?.response?.data?.detail || "";
      if (detail.includes("not_configured")) {
        toast.error(
          "Payment provider not configured yet. Please contact admin."
        );
      } else if (detail === "currency_not_supported") {
        toast.error("This course doesn't support that currency.");
      } else if (detail === "course_missing_inr_price") {
        toast.error("This course isn't priced in INR yet — ask admin to set an INR price.");
      } else if (detail === "course_missing_usd_price") {
        toast.error("This course isn't priced in USD yet — ask admin to set a USD price.");
      } else if (detail === "study_pack_missing_inr_price" || detail === "study_pack_missing_usd_price") {
        toast.error("This study pack isn't priced in that currency yet.");
      } else if (detail === "plan_disabled") {
        toast.error("This plan isn't available for this course.");
      } else {
        toast.error(detail || "Could not start checkout");
      }
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-lg" data-testid="checkout-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-amber-600" />
            Complete enrollment
          </DialogTitle>
          <DialogDescription className="text-stone-500">
            Pick a plan and currency. INR routes via Razorpay, USD via Stripe.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="text-sm">
            <div className="font-medium" data-testid="checkout-course-title">
              {course?.title}
            </div>
            <div className="text-xs text-stone-500">
              {course?.level} ·{" "}
              <span className="text-amber-700">
                {currency === "INR"
                  ? "Razorpay (UPI/cards/wallets)"
                  : "Stripe Checkout (cards)"}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Plan</Label>
              <Select value={plan} onValueChange={setPlan}>
                <SelectTrigger data-testid="checkout-plan-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PLAN_OPTIONS.map((p) => (
                    <SelectItem key={p.value} value={p.value}>
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Currency</Label>
              <Select value={currency} onValueChange={setCurrency}>
                <SelectTrigger data-testid="checkout-currency-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem
                    value="INR"
                    disabled={!availableCurrencies.INR}
                    data-testid="checkout-currency-inr"
                  >
                    INR ₹ (Razorpay){!availableCurrencies.INR ? " — not priced" : ""}
                  </SelectItem>
                  <SelectItem
                    value="USD"
                    disabled={!availableCurrencies.USD}
                    data-testid="checkout-currency-usd"
                  >
                    USD $ (Stripe){!availableCurrencies.USD ? " — not priced" : ""}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {loadingPreview && (
            <div className="py-4 text-center text-sm text-stone-500">
              <Loader2 className="mx-auto h-4 w-4 animate-spin" /> Calculating…
            </div>
          )}

          {error && (
            <div
              className="rounded-md border border-amber-300 bg-amber-50 text-amber-900 text-xs px-3 py-2 flex items-start gap-2"
              data-testid="checkout-error"
            >
              <AlertCircle className="h-3.5 w-3.5 mt-0.5" />
              <div>{error}</div>
            </div>
          )}

          {preview && !loadingPreview && (
            <div
              className="rounded-md border border-stone-200 bg-stone-50 p-3 space-y-2 text-sm"
              data-testid="checkout-preview"
            >
              <div className="flex justify-between">
                <span className="text-stone-600">Total</span>
                <span className="font-medium" data-testid="preview-total">
                  {formatMoney(preview.discounted_total, currency)}
                  {preview.discounted_total !== preview.total_amount && (
                    <span className="text-xs text-stone-400 line-through ml-2">
                      {formatMoney(preview.total_amount, currency)}
                    </span>
                  )}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-stone-600">Installments</span>
                <span data-testid="preview-installments">
                  {preview.total_installments}
                </span>
              </div>
              {preview.paid_so_far > 0 && (
                <div className="flex justify-between">
                  <span className="text-stone-600">Paid so far</span>
                  <span data-testid="preview-paid">
                    {formatMoney(preview.paid_so_far, currency)}
                  </span>
                </div>
              )}
              {nextInstallment ? (
                <div className="flex justify-between border-t border-stone-200 pt-2 mt-2">
                  <span className="font-medium">Pay now</span>
                  <span
                    className="font-semibold text-amber-700"
                    data-testid="preview-next-due"
                  >
                    {formatMoney(nextInstallment.amount, currency)}
                    <span className="text-xs text-stone-500 font-normal ml-2">
                      ({nextInstallment.label})
                    </span>
                  </span>
                </div>
              ) : (
                <div className="flex items-center gap-1 text-emerald-700">
                  <CheckCircle2 className="h-3.5 w-3.5" /> All installments paid
                </div>
              )}
            </div>
          )}

          {hasCreditsInCurrency && nextInstallment && (
            <div
              className="rounded-md border border-amber-200 bg-amber-50/60 px-3 py-2 flex items-start gap-2"
              data-testid="apply-credits-row"
            >
              <Checkbox
                id="apply-credits-toggle"
                checked={applyCredits}
                onCheckedChange={(v) => setApplyCredits(!!v)}
                data-testid="apply-credits-toggle"
                className="mt-0.5"
              />
              <div className="flex-1 space-y-0.5">
                <label
                  htmlFor="apply-credits-toggle"
                  className="text-sm font-medium text-amber-900 cursor-pointer flex items-center gap-1"
                >
                  <Coins className="h-3.5 w-3.5" />
                  Apply my credits (
                  {formatMoney(totalActive, currency)} available)
                </label>
                {applyCredits && creditToApply > 0 && (
                  <div
                    className="text-xs text-amber-800"
                    data-testid="credits-applied-summary"
                  >
                    −{formatMoney(creditToApply, currency)} in credit •
                    Total to pay:{" "}
                    <span className="font-semibold">
                      {formatMoney(amountAfterCredits, currency)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onClose?.()}
            data-testid="checkout-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={startPayment}
            disabled={
              submitting || loadingPreview || !preview || !nextInstallment
            }
            className="bg-amber-600 hover:bg-amber-700 text-white"
            data-testid="checkout-pay-button"
          >
            {submitting && (
              <Loader2 className="mr-1 h-4 w-4 animate-spin" />
            )}
            Pay {nextInstallment ? formatMoney(nextInstallment.amount, currency) : ""}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
